<template>
  <div
    class="app-message"
    v-on="$listeners"
    :class="{
      warning: warning || type === MESSAGE_TYPE_WARNING,
      success: success || type === MESSAGE_TYPE_SUCCESS,
      info: info || type === MESSAGE_TYPE_INFO,
      danger: danger || type === MESSAGE_TYPE_DANGER,
    }"
    :style="{
      backgroundColor,
      color: textColor,
    }"
  >
    <slot name="image">
      <AppImage v-if="image" :src="image" class="image" />
    </slot>

    <div class="content-wrapper">
      <h3 v-if="title || $slots.title" class="title">
        <slot name="title">
          {{ title }}
        </slot>
      </h3>

      <div v-if="content || $slots.content || $slots.default" class="content">
        <slot v-if="$slots.content" name="content" />
        <slot v-else>
          <div v-html="content" />
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import {
  MESSAGE_TYPE_WARNING,
  MESSAGE_TYPE_SUCCESS,
  MESSAGE_TYPE_INFO,
  MESSAGE_TYPE_DANGER,
} from '@/constants'
import AppImage from '@/components/App/Image.vue'
import AppIcon from '@/components/App/Icon.vue'

export default {
  name: 'AppMessage',

  inheritAttrs: false,

  components: {
    AppImage,
    AppIcon,
  },

  data() {
    this.MESSAGE_TYPE_WARNING = MESSAGE_TYPE_WARNING
    this.MESSAGE_TYPE_SUCCESS = MESSAGE_TYPE_SUCCESS
    this.MESSAGE_TYPE_INFO = MESSAGE_TYPE_INFO
    this.MESSAGE_TYPE_DANGER = MESSAGE_TYPE_DANGER

    return {}
  },

  props: {
    title: {
      type: String,
      required: false,
    },

    content: {
      type: String,
      required: false,
    },

    type: {
      type: [String, Number],
      default: 'info',
    },

    closable: {
      type: Boolean,
      default: false,
    },

    backgroundColor: {
      type: String,
      required: false,
    },

    textColor: {
      type: String,
      required: false,
    },

    warning: {
      type: Boolean,
      required: false,
      default: false,
    },

    success: {
      type: Boolean,
      required: false,
      default: false,
    },

    info: {
      type: Boolean,
      required: false,
      default: false,
    },

    error: {
      type: Boolean,
      required: false,
      default: false,
    },

    danger: {
      type: Boolean,
      required: false,
      default: false,
    },

    image: {
      type: String,
      required: false,
    },
  },
}
</script>

<style lang="scss" scoped>
@use 'sass:math';

@import '@/styles/functions.scss';
@import '@/styles/settings.scss';

$verticalPadding: math.div($defaultPadding, 2);
$sidePadding: math.div($defaultPadding, 2);

.app-message {
  position: relative;
  margin-bottom: 0.5rem;
  background-color: #f8f8f9;
  color: #333;
  padding: 0.75rem;
  border-radius: 1rem;
  width: 100%;
  display: flex;
  font-size: 0.85em;
  gap: 0.5rem;

  &:last-of-type {
    margin-bottom: $defaultPadding;
  }

  &:last-child {
    margin-bottom: 0;
  }

  > .image {
    $size: 2.5rem;
    width: $size;
    height: $size;
    display: block;
  }

  > .content-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    > .title {
      margin: 0;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      text-transform: uppercase;
    }

    > .content {
      :deep(ul) {
        padding-left: 1rem;
      }
    }
  }

  > .actions {
    margin-top: 0.5rem;
  }

  &.warning {
    $color: #fab819;
    background-color: $color;
    color: #000;
  }

  &.error,
  &.danger {
    $color: #ffcecb;
    background-color: $color;
    color: get-text-color($color);
  }

  &.info {
    $color: #deffff;
    background-color: $color;
    color: get-text-color($color);
  }

  &.success {
    $color: #d4edda;
    background-color: $color;
    color: get-text-color($color);
  }
}
</style>
